import { BuildEnvironment } from "@base/BuildEnvironment";
import { MathUtil } from "@base/math/MathUtil";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { mapCount, PartialRecord } from "@base/util/TypeUtil";
import { ConfiguratorType } from "@biz/Definitions";
import { IArticle } from "@biz/definitions/ArticleDefinitions";
import { seilStoppWinchArticleId } from "@biz/definitions/MountDefinitons";
import { Product } from "@biz/definitions/Product";
import { FeatureLevel, MeasureVisibility } from "@biz/definitions/ProductDefinitions";
import { ProductVersion } from "@biz/definitions/ProductVersion";
import { SST } from "@biz/definitions/SST";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import { IPoleData, ISunSailData, MaterialGroup, PoleId, PoleIndex, PoleMountType, PoleType, PositionMode, PylonType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { ShoppingListReducers } from "@biz/items/reducers/ShoppingListReducers";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { MotorLogic } from "@biz/logic/MotorLogic";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { ShoppingList } from "@biz/models/ShoppingList";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ISCPole } from "@biz/sc/sunSail/ISCSunSailData";
import { IColorItem } from "@biz/SunSailColors";
import { SunSailReducers } from "./SunSailReducers";

function getStartAngle(poleCount: number): number {
  switch (poleCount) {
    case 3:
      return -Math.PI * 1 / 4 - Math.PI / 12;
    case 4:
      return -Math.PI * 1 / 4;
    case 5:
      return -Math.PI * 1 / 4 - Math.PI / 10;
    case 6:
      return -Math.PI * 1 / 4 - Math.PI / 12;
  }
  return 0;
}

const cs3: IVec3[] = [
  { x: 3.059, y: 0, z: 3 },
  { x: -3.059, y: 0, z: 3 },
  { x: 0, y: 0, z: -3 },
];

const cs4: IVec3[] = [
  { x: -4.059, y: 0, z: -3.6 },
  { x: 4.059, y: 0, z: -3.6 },
  { x: 4.059, y: 0, z: 3.6 },
  { x: -4.059, y: 0, z: 3.6 },
];

const twinPoles5: IVec3[] = [
  { x: 2, y: 0, z: 0 },
  { x: -2, y: 0, z: 0 },
  { x: -2, y: 0, z: -4 },
  { x: 2, y: 0, z: -4 },
  { x: 0, y: 0, z: 3 },
];
const twinPoles6: IVec3[] = [
  { x: 2, y: 0, z: 0 },
  { x: -2, y: 0, z: 0 },
  { x: -2, y: 0, z: -4 },
  { x: 2, y: 0, z: -4 },
  { x: -2, y: 0, z: 4 },
  { x: 2, y: 0, z: 4 },
];

const sandyRect: IVec3[] = [
  { x: -2.5, y: 0, z: -1.5 },
  { x: 2.5, y: 0, z: -1.5 },
  { x: 2.5, y: 0, z: 1.5 },
  { x: -2.5, y: 0, z: 1.5 },
];

function calcInitialPolePosition(sunSailType: SunSailType, poleCount: number, pos: IVec3, poleId: PoleId): IVec3 {
  let startAngle = getStartAngle(poleCount);
  if (sunSailType === SunSailType.SolidayC) // https://plaspack.atlassian.net/browse/APP-1513
    startAngle += Math.PI;

  const pid = Poles.fromPoleId(poleId);
  if (pid.polePositionType === "center") {
    return pos;
  }

  let radius = 3;
  switch (sunSailType) {
    case SunSailType.SolidayA:
      if (poleCount === 3)
        radius = 3.0;
      else
        radius = 3.5 + (poleId === PoleIndex.B ? 0.35 : 0);
      break;
    case SunSailType.SolidayOne:
      if (poleCount === 3)
        radius = 3.0;
      else
        radius = 3.5 + (poleId === PoleIndex.B ? 0.35 : 0);
      break;
    case SunSailType.SolidayFlex:
      if (poleCount === 3)
        radius = 3.0;
      else
        radius = 4.596165933375236; // + (poleIndex === PoleIndex.B ? 0.35 : 0);
      break;

    case SunSailType.SolidayM:
      if (poleCount === 3)
        radius = 3.0;
      else
        radius = 5 + (poleId === PoleIndex.B ? 0.4 : 0);
      break;
    case SunSailType.SolidayC:
      radius = 4.63;
      break;
    case SunSailType.SolidayCS:
      return poleCount === 4 ? Vec3.add(cs4[poleId], pos) : Vec3.add(cs3[poleId], pos);
    case SunSailType.SandyRect:
      return sandyRect[poleId];
  }

  if (sunSailType === SunSailType.SolidayTwin) {
    if (poleCount === 5) {
      return Vec3.add(twinPoles5[poleId], pos);
    } else {
      return Vec3.add(twinPoles6[poleId], pos);
    }
  }
  return Vec3.addScaled(pos, Vec3.rotateY(Vec3.from(1, 0, 0), startAngle - poleId * Math.PI * 2 / poleCount), radius)
}

function calcInitialPoleType(sunSailType: SunSailType, poleCount: number, poleIndex: number): PoleType {
  if (SST.is(sunSailType, SST.AMOne) && poleCount === 3) {
    return poleIndex === 1 ? PoleType.Pole : PoleType.Wall;
  } else if (sunSailType === SunSailType.Standard) {
    return PoleType.SailOnly;
  } else if (PoleLogic.isAxle(sunSailType, poleIndex)) {
    if (SST.is(sunSailType, SST.CsXs))
      return PoleType.Wall;
    else if (SST.is(sunSailType, SST.X) && poleCount === 3)
      return PoleType.Wall;
  }
  return PoleType.Pole;
}

function calcInitialPoleHeight(sunSailType: SunSailType, poleCount: number, poleIndex: number): number {
  if (SST.is(sunSailType, SST.Sandy)) {
    return 3;
  } if (SST.is(sunSailType, SST.Flex)) {
    if (poleCount === 3) {
      return poleIndex === 1 ? 2.5 : 3.5;
    } else if (poleCount === 4) {
      return PoleLogic.isAxle(sunSailType, poleIndex) ? 3.5 : 2.5;
    }
  } else if (SST.is(sunSailType, SST.AMOne)) {
    if (poleCount === 3) {
      return poleIndex === 1 ? 2.5 : 3.5;
    } else if (poleCount === 4) {
      return poleIndex < 2 ? 2.5 : 3.5;
    }
  } if (sunSailType === SunSailType.SolidayCS) {
    switch (poleCount) {
      case 4:
        return poleIndex < 2 ? 3.5 : 3;
    }
  } else if (sunSailType === SunSailType.Concept) {
    switch (poleCount) {
      case 4:
        return poleIndex % 2 === 0 ? 3.5 : 1.5;
      case 5:
        return [4.0, 1.0, 4.0, 2.5, 1.0][poleIndex];
      case 6:
        return poleIndex % 2 === 0 ? 4.5 : 1.5;
    }
  }
  return 3.0;
}

export function calcInitialMountAngle(sunSailType: SunSailType, poleId: PoleId, mount: PoleMountType): number {
  if (Poles.fromPoleId(poleId).polePositionType === "center")
    return 0;
  if (SST.is(sunSailType, SST.Sandy))
    return 5;
  if (mount === PoleMountType.GroundPlate) {
    return 10;
  }
  return 0;
}

function calcInitialPylon(sunSailType: SunSailType, poleCount: number, poleIndex: number): PylonType | undefined {
  const poleType = calcInitialPoleType(sunSailType, poleCount, poleIndex);
  if (PoleLogic.isPylonRequired(sunSailType, poleIndex, poleType))
    return SunSailLogic.getDefaultPylonType();
  if (sunSailType === SunSailType.SolidayC && poleCount === 4 && !PoleLogic.isAxle(sunSailType, poleIndex))
    return "ZT1000";
  return undefined;
}

function calcInitialCoilUp(sunSailType: SunSailType, _poleCount: number, poleIndex: PoleId, materialGroup: MaterialGroup | undefined): string | undefined {
  if (SST.is(sunSailType, SST.AM)) {
    if (poleIndex === PoleIndex.B) {
      const coilUps = SunSailLogic._calcPossibleCoilUpsAM(materialGroup);
      const dc = seilStoppWinchArticleId;
      if (coilUps.includes(dc))
        return dc;
      if (coilUps.length > 0)
        return coilUps[0];
    }
  }
  return undefined;
}

export function calcInitialHeightChange(sunSail: ISunSailData | ISail2dForm, poleId: PoleId): string | undefined {
  const pid = Poles.fromPoleId(poleId);
  if (pid.polePositionType === "center")
    return undefined;

  if (SST.is(sunSail.sunSailType, SST.Sandy)) {
    const heightChanges = SunSailLogic.calcPossibleHeightChanges(sunSail, poleId);
    const hc = "60003A001";
    if (heightChanges.includes(hc))
      return hc;
    if (heightChanges.length > 0)
      return heightChanges[0];
  }

  if (SST.is(sunSail.sunSailType, SST.AM, SST.One) && Poles.getCount(sunSail) === 4) {
    if (poleId === PoleIndex.B) {
      const heightChanges = SunSailLogic.calcPossibleHeightChanges(sunSail, poleId);
      const hc = "00E13000052";
      if (heightChanges.includes(hc))
        return hc;
      if (heightChanges.length > 0)
        return heightChanges[0];
    }
  }
  return undefined;
}

function calcInitialMeasures(sunSailType: SunSailType, poleCount: number): PartialRecord<MeasureVisibility, boolean> {
  if (SST.is(sunSailType, SST.Sandy)) {
    return {
      bottom: true,
      // bottomSandy: true
    }
  }
  if (sunSailType === SunSailType.SolidayCS && poleCount === 4) {
    return {
      net: true,
      distOut: true,
      coilUp: true,
      netArea: true
    }
  }
  return {
    ground: true,
    // mountGround: true,
    coilUp: true,
    netArea: true
  }
}

export function createInitialSunSail(id: string, sunSailType: SunSailType, poleCount: number, version: ProductVersion, featureLevel: FeatureLevel, pos: IVec3, positionMode: PositionMode, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, sourceConfigurator: ConfiguratorType): ISunSailData {
  const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };
  const defaultClothData = ClothDataLogic.getDefaultClothData(pd, featureLevel);

  let specific: ISunSailData["specific"] | undefined;
  if (sunSailType === SunSailType.Standard) {
    const def = StandardTemplate.getDefaultStandardSize(poleCount);
    specific = {
      type: "standard",
      sizeId: def.id,
      shape: def.shape,
      center: pos,
      rotationY: 0
    }
  }

  let ssd: ISunSailData = {
    count: 1,
    itemType: "sunSail",
    version,
    buildTime: BuildEnvironment.BuildTimeAsNumber,
    featureLevel,
    sourceConfigurator,
    axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
    id,
    orderType: Product.calcOrderType(pd, sourceConfigurator).initial,
    shoppingList: ShoppingList.createEmpty(),
    sunSailType,
    sailBordersType: "individual",
    cloth: defaultClothData,
    colorId: defaultClothData ? ClothDataLogic.getInitialColorId(defaultClothData, articleMap, colorMap, featureLevel) : "",
    poles: [],
    edges: {},
    positionMode,
    specific,
    motorPosition: 0,
    twistingSet: !!SunSailLogic.isTwistingSet(sunSailType, poleCount),
    alignDistOut: SunSailLogic.calcDefaultAlignDistOut(sunSailType, poleCount),
    articleSlots: {
      revolvingRope: SunSailLogic.getInitialRevolvingRopeArticle(sunSailType),
      motor: MotorLogic.hasMotor(sunSailType) ? "00E13001883" : undefined
    },
    measures: calcInitialMeasures(sunSailType, poleCount),
  }

  ssd.poles = mapCount(poleCount, (i) => createInitialPole(ssd, poleCount, i, pos, featureLevel));
  ssd.poles = mapCount(poleCount, (i) => fillInitialPole(ssd, i));

  if (MotorLogic.hasMotor(sunSailType)) {
    ssd.shoppingList = ShoppingListReducers.setCount(ssd.shoppingList, "00E13001919", 1, { articleNumber: "00E13001919", mul: undefined, features: undefined, affiliation: undefined }); // https://plaspack.atlassian.net/browse/APP-1511
  }

  if (ssd.sunSailType === SunSailType.Standard) {
    ssd = SunSailReducers.updateStandardPositions(ssd)
  }

  if (SST.is(sunSailType, SST.CsXsTwin) && poleCount >= 4) {
    ssd = SunSailReducers.fixPosition(ssd, PoleIndex.C);
    if (poleCount > 4)
      ssd = SunSailReducers.fixPosition(ssd, PoleIndex.E);
  }
  return SunSailLogic.fixSunSailData(ssd);
}

export function createInitialPole(sunSail: ISunSailData, poleCount: number, poleId: PoleId, pos: IVec3, featureLevel: FeatureLevel): IPoleData {
  const sunSailType = sunSail.sunSailType;

  const poleType = calcInitialPoleType(sunSailType, poleCount, poleId);
  const mount = SunSailLogic.calcInitialPoleMount(sunSailType);
  const material = SunSailLogic.calcDefaultPoleMaterial(sunSailType, featureLevel);
  const p: IPoleData = {
    poleType,
    position: calcInitialPolePosition(sunSailType, poleCount, pos, poleId),
    material,
    mount,
    pylonType: calcInitialPylon(sunSailType, poleCount, poleId),
    angle: MathUtil.deg2rad(calcInitialMountAngle(sunSail.sunSailType, poleId, mount)),
    coilUpHeight: "auto",
    heightChangeCoilUpHeight: "auto",
    winchHeightChangePriority: "heightChange",
    length: calcInitialPoleHeight(sunSailType, poleCount, poleId),
    dist: { type: "in", val: "auto", },
    articleSlots: {},
  }
  return p;
}

export function createInitialSCPole(sunSail: ISail2dForm, poleCount: number, poleId: PoleId, featureLevel: FeatureLevel): ISCPole {
  const sunSailType = sunSail.sunSailType;

  const poleType = calcInitialPoleType(sunSailType, poleCount, poleId);
  const mount = SunSailLogic.calcInitialPoleMount(sunSailType);
  const material = SunSailLogic.calcDefaultPoleMaterial(sunSailType, featureLevel);
  const p: ISCPole = {
    poleType,
    material,
    mount,
    pylonType: calcInitialPylon(sunSailType, poleCount, poleId),
    angle: MathUtil.deg2rad(calcInitialMountAngle(sunSail.sunSailType, poleId, mount)),
    coilUpHeight: "auto",
    length: calcInitialPoleHeight(sunSailType, poleCount, poleId),
    articleSlots: {},
  }
  return p;
}

export function calcInitialArticleSlots<T extends (ISunSailData | ISail2dForm)>(ssd: T, poleId: PoleId): T["poles"][0]["articleSlots"] {
  const material = SunSailLogic.getPoleMaterial(ssd, poleId);
  const poleType = SunSailLogic.getPoleType(ssd, poleId);
  return {
    heightChange: calcInitialHeightChange(ssd, poleId),
    corner: SunSailLogic.getDefaultCornerType(ssd, poleId),
    coilUp: calcInitialCoilUp(ssd.sunSailType, Poles.getCount(ssd), poleId, SunSailLogic.materialGroupFromMaterial(material, poleType)),
    sailMountWinch: ssd.sunSailType === SunSailType.SolidayFlex && poleId === PoleIndex.B ? "00E13000591" : undefined,
  }
}

export function fillInitialPole<T extends (ISunSailData | ISail2dForm)>(ssd: T, poleId: PoleId): T["poles"][0] {
  let p = Poles.getPole(ssd, poleId);
  if (Object.keys(p.articleSlots || {}).length > 0) {
    throw new Error("fillInitialPole: articleSlots already filled")
  }
  const articleSlots = calcInitialArticleSlots(ssd, poleId)
  p = { ...p, articleSlots }
  return p;
}